import React from 'react';
import { useDrag, useDrop } from 'react-dnd';

import {
  Button,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';

import type { Column } from 'api/shared/models';

import { styles } from '../styles';

interface Props {
  index: number;
  column: Column;
  onClick: (column: Column) => void;
  indexTarget: number | null;
  setIndexTarget: (index: number | null) => void;
  moveRows?: (rowIndex: number, newIndex: number) => void;
  handleSort?: (column: Column) => void;
}

type DragObjectWithType = {
  id: number;
  index: number;
  type: string;
};

const OptionDragAndDrop = ({
  index,
  column,
  onClick,
  setIndexTarget,
  indexTarget,
  moveRows,
  handleSort,
}: Props) => {
  const refRow = React.useRef<HTMLAnchorElement>(null);

  const [, drop] = useDrop({
    accept: 'columnOption',
    drop(itemm, monitor) {
      if (!refRow.current) {
        return;
      }
      const item = itemm as DragObjectWithType;
      const dragIndex = item.index;
      let hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        setIndexTarget(null);
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = refRow.current.getBoundingClientRect();

      // Get horizontal middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the left
      const hoverClientY =
        clientOffset && clientOffset.y - hoverBoundingRect.top;

      if (
        dragIndex < hoverIndex &&
        hoverClientY &&
        hoverClientY < hoverMiddleY
      ) {
        hoverIndex -= 1;
      }
      // Dragging towards left
      if (
        dragIndex > hoverIndex &&
        hoverClientY &&
        hoverClientY > hoverMiddleY
      ) {
        hoverIndex += 1;
      }

      // Time to actually perform the action
      if (moveRows) {
        moveRows(dragIndex, hoverIndex);
        setIndexTarget(null);
      }

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.

      // eslint-disable-next-line no-param-reassign
      item.index = hoverIndex;
    },
    hover(itemm) {
      if (!refRow.current) {
        return;
      }
      const item = itemm as DragObjectWithType;
      const dragIndex = item.index;
      let hoverIndex = index;
      if (hoverIndex < dragIndex) {
        hoverIndex -= 1;
      }
      setIndexTarget(hoverIndex);
    },
  });

  const [, drag] = useDrag({
    item: { type: 'columnOption', index },
    canDrag: true,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      canDrag: monitor.canDrag(),
    }),
  });

  drag(drop(refRow));

  return (
    <ListItem
      key={index}
      disablePadding
      ref={refRow as any}
      sx={(theme: Theme) => ({
        ...styles.listItem,
        ...(index === indexTarget || (indexTarget === -1 && index === 0)
          ? indexTarget === -1
            ? styles.targetRowTop(theme)
            : styles.targetRowBottom(theme)
          : {}),
      })}
    >
      <Button
        component="div"
        disableTouchRipple
        variant="outlined"
        sx={(theme: Theme) => ({
          ...styles.listItemButton(theme),
          cursor: 'move',
        })}
      >
        <DragIndicatorOutlinedIcon
          id="draggable-dialog-title"
          style={{ marginRight: 8 }}
        />
        <ListItemText primary={column?.label} />

        <Tooltip
          title={
            column.direction === 'asc'
              ? 'Cambiar a descendente'
              : 'Cambiar a ascendente'
          }
          key={column.direction}
        >
          <IconButton
            size="small"
            onClick={() => handleSort && handleSort(column)}
          >
            {column.direction === 'asc' ? (
              <ArrowUpwardIcon />
            ) : (
              <ArrowDownwardIcon />
            )}
          </IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => onClick(column)}>
          <CloseIcon />
        </IconButton>
      </Button>
    </ListItem>
  );
};

export default OptionDragAndDrop;
